<!-- Chat form for entering and submitting a blood pressure (systolic/diastolic) answer. -->

<script lang="ts" setup>
import { assert } from '@nzyme/utils/assert.js';
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useForm } from '@nzyme/vue-forms/useForm.js';
import { useFormFields } from '@nzyme/vue-forms/useFormFields.js';
import { maxValueValidator } from '@nzyme/vue-forms/validators/maxValueValidator.js';
import { minValueValidator } from '@nzyme/vue-forms/validators/minValueValidator.js';
import { requiredValidator } from '@nzyme/vue-forms/validators/requiredValidator.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { ref } from 'vue';

import type { BloodPressureInputConfig, BloodPressureInputValue } from '@sigma/common/forms/BloodPressureInput.js';

import Button from '../../Button.vue';
import FieldInfo from '../../forms/FieldInfo.vue';
import NumberInput from '../../forms/NumberInput.vue';
import * as l from './ChatBloodPressureInput.loc.js';

interface Props {
  config: BloodPressureInputConfig;
}

interface Emits {
  submit: [value: BloodPressureInputValue];
}

defineProps<Props>();
defineEmits<Emits>();

const t = useTranslate();
const emitAsync = useEmitAsync<Emits>();

const form = useForm({
  systolic: null as number | null,
  diastolic: null as number | null,
});

const fields = useFormFields(form, {
  systolic: [
    requiredValidator({ message: () => t(l.enterSystolic) }),
    minValueValidator({ minValue: 30, message: () => t(l.systolicRange) }),
    maxValueValidator({ maxValue: 300, message: () => t(l.systolicRange) }),
  ],
  diastolic: [
    requiredValidator({ message: () => t(l.enterDiastolic) }),
    minValueValidator({ minValue: 30, message: () => t(l.diastolicRange) }),
    maxValueValidator({ maxValue: 300, message: () => t(l.diastolicRange) }),
  ],
});

const formError = ref<string | null>(null);

async function handleSubmit() {
  formError.value = null;

  const valid = await form.validate();
  if (!valid) {
    return;
  }

  assert(form.value.systolic);
  assert(form.value.diastolic);
  let systolic = form.value.systolic;
  let diastolic = form.value.diastolic;

  // Ensure systolic > diastolic
  if (systolic < diastolic) {
    [systolic, diastolic] = [diastolic, systolic];
  }

  await emitAsync('submit', {
    type: 'BLOOD_PRESSURE',
    systolic,
    diastolic,
  });
}
</script>

<template>
  <Form
    :class="css.formContainer"
    @submit="handleSubmit"
  >
    <div :class="css.inputsRow">
      <div :class="css.inputGroup">
        <NumberInput
          tid="systolic-field"
          :field="fields.systolic"
          :placeholder="t(l.systolicPlaceholder)"
          :min="50"
          :max="300"
        >
          <template #addon>
            <span :class="css.unit">mmHg</span>
          </template>
        </NumberInput>
      </div>

      <span :class="css.separator">/</span>

      <div :class="css.inputGroup">
        <NumberInput
          tid="diastolic-field"
          :field="fields.diastolic"
          :placeholder="t(l.diastolicPlaceholder)"
          :min="30"
          :max="200"
        >
          <template #addon>
            <span :class="css.unit">mmHg</span>
          </template>
        </NumberInput>
      </div>
    </div>

    <FieldInfo
      v-if="formError"
      type="error"
    >
      {{ formError }}
    </FieldInfo>

    <Button
      tid="chat-question-submit"
      variant="primary"
      size="large"
      color="black"
      rounded
      :class="css.submitButton"
      type="submit"
    >
      {{ t(l.submitButton) }}
    </Button>
  </Form>
</template>

<style lang="scss" module="css">
.formContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.inputsRow {
  display: flex;
  align-items: flex-start;
  gap: $spacing-08;
}

.inputGroup {
  flex: 1;
}

.separator {
  font-size: $font-size-24;
  color: $color-gray-600;
  padding-top: $spacing-06;
}

.unit {
  font-size: $font-size-14;
  color: $color-gray-600;
  white-space: nowrap;
}

.submitButton {
  margin-top: $spacing-08;
}
</style>
